import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiSave, FiArrowLeft } from 'react-icons/fi';
import technicianService from '../services/technicianService';
import Input from '../components/Input';
import Button from '../components/Button';
import './TechnicianForm.css';

const TechnicianForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    employeeId: '',
    experience: '',
    salary: '',
    status: 'available',
    expertise: [],
  });

  const [selectedExpertise, setSelectedExpertise] = useState([]);

  const expertiseOptions = [
    'AC Repair',
    'Heating Systems',
    'Ventilation',
    'Refrigeration',
    'Installation',
    'Maintenance',
    'Electrical',
    'Plumbing',
  ];

  useEffect(() => {
    if (isEdit) {
      fetchTechnician();
    }
  }, [id]);

  const fetchTechnician = async () => {
    try {
      const response = await technicianService.getById(id);
      const tech = response.data;
      setFormData({
        name: tech.name,
        email: tech.email || '',
        phone: tech.phone,
        employeeId: tech.employeeId,
        experience: tech.experience || '',
        salary: tech.salary || '',
        status: tech.status,
      });
      setSelectedExpertise(tech.expertise || []);
    } catch (error) {
      toast.error('Failed to fetch technician details');
      navigate('/technicians');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleExpertiseToggle = (skill) => {
    if (selectedExpertise.includes(skill)) {
      setSelectedExpertise(selectedExpertise.filter((s) => s !== skill));
    } else {
      setSelectedExpertise([...selectedExpertise, skill]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const submitData = {
      ...formData,
      expertise: selectedExpertise,
      experience: Number(formData.experience),
      salary: Number(formData.salary),
    };

    try {
      if (isEdit) {
        await technicianService.update(id, submitData);
        toast.success('Technician updated successfully');
      } else {
        await technicianService.create(submitData);
        toast.success('Technician added successfully');
      }
      navigate('/technicians');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <div className="form-header">
        <button onClick={() => navigate('/technicians')} className="back-button">
          <FiArrowLeft /> Back
        </button>
        <h2 className="form-title">
          {isEdit ? 'Edit Technician' : 'Add New Technician'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="form-card">
        <div className="form-section">
          <h3 className="section-title">Basic Information</h3>
          <div className="form-grid">
            <Input
              label="Full Name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="John Doe"
              required
            />

            <Input
              label="Employee ID"
              type="text"
              name="employeeId"
              value={formData.employeeId}
              onChange={handleChange}
              placeholder="TECH001"
              required
              disabled={isEdit}
            />

            <Input
              label="Email Address"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="john@example.com"
            />

            <Input
              label="Phone Number"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="9876543210"
              required
            />
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Professional Details</h3>
          <div className="form-grid">
            <Input
              label="Experience (Years)"
              type="number"
              name="experience"
              value={formData.experience}
              onChange={handleChange}
              placeholder="5"
              min="0"
            />

            <Input
              label="Monthly Salary (₹)"
              type="number"
              name="salary"
              value={formData.salary}
              onChange={handleChange}
              placeholder="30000"
              min="0"
            />

            <div className="input-group">
              <label className="input-label">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="input-field"
              >
                <option value="available">Available</option>
                <option value="on_site">On Site</option>
                <option value="off_duty">Off Duty</option>
                <option value="on_leave">On Leave</option>
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Expertise</h3>
          <p className="section-description">Select all areas of expertise</p>
          <div className="expertise-grid">
            {expertiseOptions.map((skill) => (
              <label key={skill} className="expertise-checkbox">
                <input
                  type="checkbox"
                  checked={selectedExpertise.includes(skill)}
                  onChange={() => handleExpertiseToggle(skill)}
                />
                <span className="checkbox-label">{skill}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/technicians')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            icon={<FiSave />}
            loading={loading}
          >
            {isEdit ? 'Update Technician' : 'Add Technician'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TechnicianForm;
